/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React from "react";
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { BaseFieldProps } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

type FieldNumberProps = BaseFieldProps &
    Omit<React.ComponentProps<typeof Input>, "type" | "value" | "onChange"> & {
        /** Ondalık hane sayısı: adet=0, miktar=3-4 */
        maxDecimals?: number;
        /** Negatif sayı izni (default: false) */
        allowNegative?: boolean;
        /** Boş değer izinli (default: true) */
        allowEmpty?: boolean;
    };

function sanitizeDisplay(
    raw: string,
    opts: { allowNegative: boolean; maxDecimals?: number; allowEmpty: boolean }
) {
    const { allowNegative, maxDecimals, allowEmpty } = opts;

    // 1) '.' -> ',' (UI standardı)
    let s = raw.replace(/\./g, ",");

    // 2) sadece izinli karakterler
    s = s.replace(/[^0-9,\-]/g, "");

    // 3) negatif kontrolü
    if (!allowNegative) {
        s = s.replace(/-/g, "");
    } else {
        s = s.replace(/(?!^)-/g, ""); // sadece baştaki '-'
    }

    // ✅ 4) Ondalık yoksa virgül hiç kabul etme
    if (maxDecimals === 0) {
        s = s.split(",")[0];
    } else {
        // 5) tek virgül
        const firstComma = s.indexOf(",");
        if (firstComma !== -1) {
            s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, "");
        }

        // 6) maxDecimals uygula
        if (maxDecimals !== undefined && firstComma !== -1) {
            const [i, f = ""] = s.split(",");
            s = i + "," + f.slice(0, Math.max(0, maxDecimals));
        }
    }

    // 7) allowEmpty=false ise tamamen boş kalmasın (blur'da da ayrıca normalize ediliyor)
    if (!allowEmpty && (s === "" || s === "-" || s === "," || s === "-,")) {
        s = "0";
    }

    return s;
}


function displayToState(display: string) {
    // UI: "3,27"  -> state: "3.27"
    // UI: ""      -> state: ""
    return (display ?? "").replace(",", ".");
}

function stateToDisplay(state: string) {
    // state: "3.27" -> UI: "3,27"
    return (state ?? "").replace(".", ",");
}

function normalizeOnBlur(display: string, opts: { allowEmpty: boolean }) {
    let s = display;

    // "12," gibi yarım kaldıysa -> "12"
    if (s.endsWith(",")) s = s.slice(0, -1);

    // "," ile başladıysa -> "0,..." (örn ",5" -> "0,5")
    if (s.startsWith(",")) s = "0" + s;
    if (s.startsWith("-,")) s = s.replace("-,", "-0,");

    // boşsa
    if (!opts.allowEmpty && s === "") s = "0";

    return s;
}

export function FieldNumber({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    maxDecimals,
    allowNegative = false,
    allowEmpty = true,
    placeholder,
    inputMode,
    ...rest
}: FieldNumberProps) {
    return (
        <FormField
            name={name}
            render={({ field }) => {
                let stateStr = (field.value ?? "").toString();

                // maxDecimals=0 iken backend "20.00" gibi ondalıklı gönderdiyse,
                // display'e çevirmeden ÖNCE sayıyı tam sayıya normalize et
                if (maxDecimals === 0 && stateStr.includes(".")) {
                    const num = parseFloat(stateStr);
                    if (!isNaN(num)) {
                        stateStr = Math.round(num).toString();
                    }
                }

                const displayStr = stateToDisplay(stateStr);

                return (
                    <FormItem className={className}>
                        <FormFieldLabel
                            label={label}
                            labelType={labelType}
                            tooltipContent={tooltipContent}
                        />
                        <FormControl>
                            <Input
                                {...rest}
                                ref={field.ref}
                                type="text"
                                inputMode={inputMode ?? (maxDecimals === 0 ? "numeric" : "decimal")}
                                placeholder={placeholder}
                                value={displayStr}
                                onChange={(e) => {
                                    const nextDisplay = sanitizeDisplay(e.target.value, {
                                        allowNegative,
                                        maxDecimals,
                                        allowEmpty,
                                    });

                                    field.onChange(displayToState(nextDisplay)); // ✅ state "3.27"
                                }}
                                onBlur={(e) => {
                                    field.onBlur();

                                    const cleanedDisplay = normalizeOnBlur(
                                        sanitizeDisplay(e.target.value, { allowNegative, maxDecimals, allowEmpty }),
                                        { allowEmpty }
                                    );

                                    // blur'da final normalize -> state'e yaz
                                    field.onChange(displayToState(cleanedDisplay));

                                    rest.onBlur?.(e);
                                }}
                            />
                        </FormControl>

                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
